import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Header        from './components/Header/Header';
import Footer        from './components/Footer/Footer';
import PageRenderer  from './PageRenderer';
import ShopPage      from './components/ShopPage/ShopPage';
import WishlistPage       from './components/WishlistPage/WishlistPage';
import ProductDetailPage  from './components/ProductDetailPage/ProductDetailPage';
import CartPage           from './components/CartPage/CartPage';
import CheckoutPage       from './components/CheckoutPage/CheckoutPage';
import AccountPage        from './components/AccountPage/AccountPage';
import ContactPage        from './components/ContactPage/ContactPage';
import BlogListPage       from './components/BlogListPage/BlogListPage';
import BlogDetailPage     from './components/BlogDetailPage/BlogDetailPage';
import { TEMPLATE_REGISTRY } from './templates/index';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

const td = window.themeData ?? {};

function mount(id, Component, props = {}) {
  const el = document.getElementById(id);
  if (!el) return;
  createRoot(el).render(
    <QueryClientProvider client={queryClient}>
      <Component {...props} />
    </QueryClientProvider>
  );
}

mount('anvi-header',      Header,  { cartCount: td.cartCount, isLoggedIn: td.isLoggedIn });
mount('anvi-footer-root', Footer,  {});

// Dynamic page renderer — reads saved section layout from REST API
const pageRoot = document.getElementById('anvi-page-root');
if (pageRoot) {
  const postId = parseInt(pageRoot.dataset.postId ?? '0', 10);
  createRoot(pageRoot).render(
    <QueryClientProvider client={queryClient}>
      <PageRenderer postId={postId} />
    </QueryClientProvider>
  );
}

// WooCommerce shop page
mount('anvi-shop', ShopPage, {});

// Wishlist page
mount('anvi-wishlist', WishlistPage, {});

// Product detail page
mount('anvi-product-detail', ProductDetailPage, {});

// Cart page
mount('anvi-cart', CartPage, {});

// Checkout page
mount('anvi-checkout', CheckoutPage, {});

// Account page
mount('anvi-account', AccountPage, {});

// Contact page
mount('anvi-contact', ContactPage, {});

// Blog list page (home.php, archive.php, page.php /blog/)
const blogEl = document.getElementById('anvi-blog');
if (blogEl) {
  const initialCategory = blogEl.dataset.category ?? '';
  createRoot(blogEl).render(
    <QueryClientProvider client={queryClient}>
      <BlogListPage initialCategory={initialCategory} />
    </QueryClientProvider>
  );
}

// Blog single post
const blogPostEl = document.getElementById('anvi-blog-post');
if (blogPostEl) {
  const postId = parseInt(blogPostEl.dataset.postId ?? '0', 10);
  createRoot(blogPostEl).render(
    <QueryClientProvider client={queryClient}>
      <BlogDetailPage postId={postId} />
    </QueryClientProvider>
  );
}

// Hydrate section-block elements dropped inside custom rows.
// PHP outputs <div class="anvi-section-block" data-template-id="hero-slider" data-props='...'></div>
// Saved props from data-props override template defaults so builder changes are reflected.
document.querySelectorAll('.anvi-section-block[data-template-id]').forEach((el) => {
  const templateId = el.dataset.templateId;
  const template   = TEMPLATE_REGISTRY.find((t) => t.id === templateId);
  if (!template?.component) return;
  const Component  = template.component;

  let savedProps = {};
  try { if (el.dataset.props) savedProps = JSON.parse(el.dataset.props); } catch { /* ignore */ }

  const finalProps = { ...(template.defaultProps ?? {}), ...savedProps };

  createRoot(el).render(
    <QueryClientProvider client={queryClient}>
      <Component {...finalProps} />
    </QueryClientProvider>
  );
});
